import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Images, Trash2, Plus, Upload, GripVertical, Loader2, ChevronsUpDown, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { getImageUrl } from "../../utils/imageUtils";
import InfinitySlider from "../customer-infinity/InfinitySlider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mirrors DEFAULT_THEME in api/src/modules/customers-infinity/infinityCustomer.controller.js
// so the preview matches production even before the platform doc is seeded.
const FALLBACK_THEME = { primary_color: "#2E72BC", secondary_color: "#0C447C", accent_color: "#E0A93C" };

// Stable per-row identity. Array indexes break React reconciliation once rows
// can be dragged, so every slide carries a local uid that is stripped on save.
let uidSeq = 0;
const withUid = (s) => ({ ...s, _uid: `slide-${++uidSeq}` });

/**
 * Super-admin management for the Infinity Touch customer login slider.
 *
 * A slide is one of two kinds (TR-22), chosen by the "Feature a product" switch:
 *  - PRODUCT slide — a featured Infinity product + the keepsake-card image
 *    (`background_image_url`) + an optional caption. Clickable.
 *  - BANNER slide  — full-bleed artwork (`banner_image_url`) + an optional caption.
 *    No product, nothing to click.
 *
 * Persisted on the platform doc as `infinity_login_slider` via
 * PUT /admin/platform-settings, and read back by the customer site through
 * GET /api/infinity/customer/config (which drops inactive slides and sorts by
 * `order` — the same filter the preview panel applies here).
 */
export default function AdminInfinityLoginSlider() {
  const [slides, setSlides] = useState([]);
  const [theme, setTheme] = useState(FALLBACK_THEME);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [uploadingUid, setUploadingUid] = useState(null);

  // Product picker
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productQuery, setProductQuery] = useState("");
  const [pickerOpen, setPickerOpen] = useState(null); // uid of the open picker

  // Names for products referenced by saved slides. The picker only holds one
  // page of results, so without this a saved slide whose product is outside
  // that page would show a raw product_id instead of its name.
  const [slideProducts, setSlideProducts] = useState({});

  // Drag & drop. `dragHandleUid` gates `draggable` on the row: a permanently
  // draggable container swallows text selection in the caption input, so a row
  // only becomes draggable while its grip is held.
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragHandleUid, setDragHandleUid] = useState(null);
  const dragCounter = useRef({});

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });

  useEffect(() => {
    (async () => {
      try {
        const settings = await axios.get(`${API}/admin/platform-settings`, authHeaders());
        const saved = settings.data?.infinity_login_slider || [];
        setSlides(saved.map(withUid));
        setTheme({ ...FALLBACK_THEME, ...(settings.data?.infinity_customer_theme || {}) });

        // Resolve the referenced products so saved slides show real names.
        const ids = [...new Set(saved.map((s) => s.product_id).filter(Boolean))];
        const resolved = await Promise.all(ids.map((id) =>
          axios.get(`${API}/products/${id}`, authHeaders())
            .then((r) => [id, { product_id: id, name: r.data?.name, image_url: r.data?.image_url }])
            .catch(() => null)));
        setSlideProducts(Object.fromEntries(resolved.filter(Boolean)));
      } catch {
        toast.error("Failed to load slider");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Only Infinity-channel parent products are valid here: the customer product
  // page prices anything else on the edisplay channel instead of rejecting it,
  // so scoping the picker is what keeps a slide from opening at a wrong price.
  const loadProducts = useCallback(async (keyword) => {
    setProductsLoading(true);
    try {
      const r = await axios.get(`${API}/admin/products/catalog`, {
        params: { channel: "infinity", only_parents: "true", limit: 50, ...(keyword ? { keyword } : {}) },
        ...authHeaders(),
      });
      setProducts(r.data?.data || []);
    } catch {
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => loadProducts(productQuery.trim()), productQuery ? 300 : 0);
    return () => clearTimeout(t);
  }, [productQuery, loadProducts]);

  const productById = (id) => products.find((p) => p.product_id === id) || slideProducts[id];

  // --- Mutations ---
  const update = (uid, patch) => {
    setSlides((s) => s.map((sl) => (sl._uid === uid ? { ...sl, ...patch } : sl)));
    setHasChanges(true);
  };

  const addSlide = () => {
    setSlides((s) => [...s, withUid({ product_id: "", background_image_url: "", banner_image_url: "", show_product: true, text: "", order: s.length, is_active: true })]);
    setHasChanges(true);
  };

  const removeSlide = (uid) => {
    if (!window.confirm("Remove this slide from the login page?")) return;
    setSlides((s) => s.filter((sl) => sl._uid !== uid));
    setHasChanges(true);
  };

  // Picking a product prefills the image and caption from the product itself —
  // that is the slide content in almost every case, and both stay editable.
  const pickProduct = (uid, product) => {
    setSlides((s) => s.map((sl) => (sl._uid === uid
      ? {
        ...sl,
        product_id: product.product_id,
        background_image_url: sl.background_image_url || product.image_url || "",
        text: sl.text || product.name || "",
      }
      : sl)));
    // Remember it, so the name survives a later search that pages it out.
    setSlideProducts((m) => ({ ...m, [product.product_id]: product }));
    setHasChanges(true);
    setPickerOpen(null);
    setProductQuery("");
  };

  // `field` selects which image this upload fills: the product-card image
  // (background_image_url) or the full-bleed banner (banner_image_url).
  const uploadImage = async (uid, e, field = "background_image_url") => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please upload an image");
    setUploadingUid(`${uid}:${field}`);
    try {
      const fd = new FormData();
      fd.append("hero", file);
      const r = await axios.post(`${API}/upload/hero`, fd, authHeaders());
      update(uid, { [field]: r.data.url });
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploadingUid(null);
      e.target.value = "";
    }
  };

  // --- Drag & Drop (same interaction as Featured Collections) ---
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";

    // Styled clone so the drag image shows what is being moved.
    const el = e.currentTarget;
    const clone = el.cloneNode(true);
    Object.assign(clone.style, {
      position: "absolute", top: "-9999px", left: "-9999px",
      width: Math.min(el.offsetWidth, 800) + "px", maxWidth: "800px",
      overflow: "hidden", opacity: "0.85", transform: "rotate(1deg) scale(1.02)",
      boxShadow: "0 12px 40px rgba(0,0,0,0.18)", borderRadius: "12px",
      border: "2px solid #6366f1", background: "white", zIndex: "9999",
    });
    document.body.appendChild(clone);
    e.dataTransfer.setDragImage(clone, Math.min(el.offsetWidth, 800) / 2, el.offsetHeight / 2);
    requestAnimationFrame(() => document.body.removeChild(clone));
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragCounter.current[index] = (dragCounter.current[index] || 0) + 1;
    if (index !== draggedIndex) setDragOverIndex(index);
  };

  const handleDragLeave = (e, index) => {
    dragCounter.current[index] = (dragCounter.current[index] || 0) - 1;
    if (dragCounter.current[index] <= 0) {
      dragCounter.current[index] = 0;
      if (dragOverIndex === index) setDragOverIndex(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    dragCounter.current = {};
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }
    setSlides((s) => {
      const updated = [...s];
      const [dragged] = updated.splice(draggedIndex, 1);
      updated.splice(dropIndex, 0, dragged);
      return updated;
    });
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragHandleUid(null);
    setHasChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragHandleUid(null);
    dragCounter.current = {};
  };

  // --- Save ---
  const save = async () => {
    // The visual is the point, so every slide needs the artwork its own kind uses:
    // a product slide needs the keepsake-card image, a banner slide needs the banner.
    // A product slide with no product would render as a dead card, so that stays
    // blocked — but only for product slides (TR-22).
    if (slides.some((s) => (s.show_product !== false ? !s.background_image_url : !s.banner_image_url))) {
      return toast.error("Every slide needs an image — a product image, or a banner when the product is switched off");
    }
    if (slides.some((s) => s.show_product !== false && !s.product_id)) {
      return toast.error("Every product slide needs a featured product — without one the slide is not clickable. Switch off \"Feature a product\" to make it a banner instead.");
    }

    const ids = slides.filter((s) => s.show_product !== false).map((s) => s.product_id);
    if (new Set(ids).size !== ids.length) toast.warning("Two or more slides feature the same product");

    setSaving(true);
    try {
      const payload = slides.map(({ _uid, ...s }, idx) => ({ ...s, order: idx }));
      await axios.put(`${API}/admin/platform-settings`, { infinity_login_slider: payload }, authHeaders());
      toast.success("Slider saved");
      setHasChanges(false);
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  // Same filter getConfig applies, so the preview is what customers will see —
  // a slide qualifies on either image, since banner slides have no card image.
  const previewSlides = slides.filter((s) => s.is_active !== false && (s.background_image_url || s.banner_image_url));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-infinity-login-slider">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Images className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Customer Login Slider</h2>
            <p className="text-sm text-slate-500">Featured keepsakes and banners on the customer login page (customer.infinitytouch.app).</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">Unsaved changes</Badge>}
          <Button variant="outline" onClick={addSlide} className="gap-2 border-primary/20 text-primary hover:bg-primary/10">
            <Plus className="w-4 h-4" /> Add slide
          </Button>
          <Button onClick={save} disabled={saving || !hasChanges} className="gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_380px] gap-6 items-start">
        {/* Slide list */}
        <div className="space-y-4">
          {slides.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
              <Images className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="font-medium text-slate-900">No slides yet</p>
              <p className="text-sm text-slate-500 mt-1">Add a slide to feature a keepsake on the login page.</p>
              <Button variant="outline" onClick={addSlide} className="mt-4 gap-2">
                <Plus className="w-4 h-4" /> Add slide
              </Button>
            </div>
          ) : (
            slides.map((s, i) => {
              const picked = productById(s.product_id);
              // Legacy slides predate the flag and are all product slides.
              const isProductSlide = s.show_product !== false;
              const productUploading = uploadingUid === `${s._uid}:background_image_url`;
              const bannerUploading = uploadingUid === `${s._uid}:banner_image_url`;
              return (
                <div
                  key={s._uid}
                  draggable={dragHandleUid === s._uid}
                  onDragStart={(e) => handleDragStart(e, i)}
                  onDragEnter={(e) => handleDragEnter(e, i)}
                  onDragLeave={(e) => handleDragLeave(e, i)}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, i)}
                  onDragEnd={handleDragEnd}
                  className={cn(
                    "bg-white rounded-xl shadow-sm border p-4 grid md:grid-cols-[140px_1fr_auto] gap-4 items-start transition-all",
                    draggedIndex === i && "opacity-40",
                    dragOverIndex === i && "ring-2 ring-primary ring-offset-2"
                  )}
                >
                  {/* Image — framed exactly as the live slider renders it: a square
                      keepsake card for product slides, a wide full-bleed banner for
                      banner slides. Each slide only needs the one it actually uses. */}
                  <div>
                    {isProductSlide ? (
                      <>
                        <div className="aspect-square rounded-lg bg-slate-50 border overflow-hidden flex items-center justify-center relative">
                          {s.background_image_url ? (
                            <>
                              <img src={getImageUrl(s.background_image_url)} alt="" className="w-full h-full object-contain" />
                              <button
                                type="button"
                                onClick={() => update(s._uid, { background_image_url: "" })}
                                className="absolute top-1.5 right-1.5 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                                title="Remove image"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </>
                          ) : (
                            <Images className="w-8 h-8 text-slate-300" />
                          )}
                        </div>
                        <label className="inline-flex items-center gap-1.5 text-xs text-primary cursor-pointer mt-2 hover:underline">
                          {productUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                          {productUploading ? "Uploading…" : "Product image"}
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(s._uid, e, "background_image_url")} disabled={productUploading} />
                        </label>
                      </>
                    ) : (
                      <>
                        <div className="aspect-video rounded-lg bg-slate-50 border overflow-hidden flex items-center justify-center relative">
                          {s.banner_image_url ? (
                            <>
                              <img src={getImageUrl(s.banner_image_url)} alt="" className="w-full h-full object-cover" />
                              <button
                                type="button"
                                onClick={() => update(s._uid, { banner_image_url: "" })}
                                className="absolute top-1.5 right-1.5 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                                title="Remove banner"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </>
                          ) : (
                            <Images className="w-8 h-8 text-slate-300" />
                          )}
                        </div>
                        <label className="inline-flex items-center gap-1.5 text-xs text-primary cursor-pointer mt-2 hover:underline">
                          {bannerUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                          {bannerUploading ? "Uploading…" : "Banner image"}
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(s._uid, e, "banner_image_url")} disabled={bannerUploading} />
                        </label>
                        <p className="text-[11px] text-slate-400 mt-1">Fills the whole panel. Landscape artwork works best.</p>
                      </>
                    )}
                  </div>

                  {/* Fields */}
                  <div className="space-y-3">
                    {/* Switch this off to turn the slide into a plain banner: no
                        keepsake card, no product to pick, nothing clickable. */}
                    <div className="flex items-center gap-2">
                      <Switch checked={isProductSlide} onCheckedChange={(v) => update(s._uid, { show_product: v })} />
                      <span className="text-sm text-slate-600">Feature a product</span>
                      {!isProductSlide && <Badge variant="outline" className="text-[10px] text-slate-500">Banner slide</Badge>}
                    </div>

                    {isProductSlide ? (
                    <div className="space-y-1">
                      <Label className="text-xs">Featured product</Label>
                      <Popover open={pickerOpen === s._uid} onOpenChange={(o) => { setPickerOpen(o ? s._uid : null); if (!o) setProductQuery(""); }}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
                            <span className={cn("truncate", !s.product_id && "text-slate-400")}>
                              {picked?.name || s.product_id || "Select an Infinity product"}
                            </span>
                            <ChevronsUpDown className="w-4 h-4 opacity-50 shrink-0" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
                          {/* Search runs on the server, so cmdk must not filter again. */}
                          <Command shouldFilter={false}>
                            <CommandInput placeholder="Search Infinity products…" value={productQuery} onValueChange={setProductQuery} />
                            <CommandList>
                              {productsLoading ? (
                                <div className="flex items-center justify-center py-6 text-slate-400">
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                </div>
                              ) : (
                                <>
                                  <CommandEmpty>No Infinity products found.</CommandEmpty>
                                  <CommandGroup>
                                    {products.map((p) => (
                                      <CommandItem key={p.product_id} value={p.product_id} onSelect={() => pickProduct(s._uid, p)}>
                                        <div className="w-8 h-8 rounded bg-slate-50 border overflow-hidden shrink-0 mr-2 flex items-center justify-center">
                                          {p.image_url ? <img src={getImageUrl(p.image_url)} alt="" className="w-full h-full object-contain" /> : null}
                                        </div>
                                        <span className="truncate flex-1">{p.name}</span>
                                        {p.is_active === false && <Badge variant="outline" className="ml-2 text-[10px] text-slate-500">Inactive</Badge>}
                                        {s.product_id === p.product_id && <Check className="w-4 h-4 ml-2 text-primary shrink-0" />}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </>
                              )}
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                    ) : null}

                    <div className="space-y-1">
                      <Label className="text-xs">Caption <span className="text-slate-400 font-normal">(optional)</span></Label>
                      <Input
                        value={s.text || ""}
                        onChange={(e) => update(s._uid, { text: e.target.value })}
                        placeholder={isProductSlide ? "Sterling Heart Pendant" : "In loving memory"}
                      />
                      <p className="text-[11px] text-slate-400">
                        {isProductSlide
                          ? "Shown under the keepsake image on a single line."
                          : "Replaces the main headline for this slide. Leave blank to use default."}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <Switch checked={s.is_active !== false} onCheckedChange={(v) => update(s._uid, { is_active: v })} />
                      <span className="text-sm text-slate-600">Active</span>
                    </div>
                  </div>

                  {/* Handle + delete */}
                  <div className="flex flex-col items-center gap-3 text-slate-400">
                    <GripVertical
                      className="w-4 h-4 cursor-grab active:cursor-grabbing"
                      title="Drag to reorder"
                      onMouseDown={() => setDragHandleUid(s._uid)}
                      onMouseUp={() => setDragHandleUid(null)}
                    />
                    <span className="text-xs font-medium text-slate-400">{i + 1}</span>
                    <button
                      type="button"
                      onClick={() => removeSlide(s._uid)}
                      className="text-red-500 hover:text-red-600"
                      title="Delete slide"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Live preview — the real customer slider on the real gradient */}
        <div className="lg:sticky lg:top-6">
          <div className="bg-white rounded-xl shadow-sm border p-4 space-y-3">
            <div>
              <h3 className="font-semibold text-slate-900">Live preview</h3>
              <p className="text-xs text-slate-500">Active slides, in order, exactly as the login page renders them.</p>
            </div>
            <div
              className="rounded-lg overflow-hidden h-[460px]"
              style={{ background: `linear-gradient(135deg, ${theme.primary_color}, ${theme.secondary_color})` }}
            >
              {previewSlides.length ? (
                <InfinitySlider slides={previewSlides} theme={theme} />
              ) : (
                <div className="h-full flex items-center justify-center text-white/70 text-sm px-6 text-center">
                  No active slides — the login page falls back to featured Infinity products.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
